import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
// eslint-disable-next-line
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
// eslint-disable-next-line
import CareerAssessment from './components/Assessment/CareerAssessment';
// eslint-disable-next-line
import SubjectAssessment from './components/Assessment/SubjectAssessment';
// eslint-disable-next-line
import SubjectPart3Assessment from './components/Assessment/SubjectPart3Assessment';
// eslint-disable-next-line
import LearningAssessment from './components/Assessment/LearningAssessment';
// eslint-disable-next-line
import Dashboard from './components/pages/dashboard';
// eslint-disable-next-line
import Dashboard_Enrich from './components/pages/dashboard_enrich';
import Login from './components/AdminDashboard/Login';
import useAuth from './hooks/useAuth';
// eslint-disable-next-line
import AuthForm from './components/AuthForm';
// eslint-disable-next-line
import SubjectScoresPage from './components/scores/SubjectScoresPage';
// eslint-disable-next-line
import CareerScoresPage from './components/scores/CareerScoresPage';
// eslint-disable-next-line
import LearningScoresPage from './components/scores/LearningScoresPage';
// eslint-disable-next-line
import { fetchProgress } from './api/assessmentApi';
import WhitelistManager from './components/AdminDashboard/WhitelistManager';
import CoachScoresPage from './components/scores/CoachScoresPage';
import { ModalProvider } from './constants/ModalContext';
import { UIFeedbackProvider } from "./components/UIFeedback";

// Helper function to lazy-load team participation data
export async function getTeamParticipation(userId, token) {
  const cacheKey = `team_participation_${userId}`;
  const cached = localStorage.getItem(cacheKey);
  const timestamp = localStorage.getItem(`${cacheKey}_timestamp`);
  
  // Check if we have valid cached data (within 1 hour)
  if (cached && timestamp) {
    const age = Date.now() - parseInt(timestamp);
    if (age < 3600000) { // 1 hour TTL
      return JSON.parse(cached);
    }
  }
  
  try {
    const response = await axios.get(`/api/users/${userId}/app_role`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    const data = {
      last_team_participation: response.data.last_team_participation || null
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(data));
    localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
    
    return data;
  } catch (err) {
    console.error('Failed to fetch team participation:', err);
    return { last_team_participation: null };
  }
}

const ACCESS_CACHE_KEY = 'access_check_cache';
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

// Helper functions for access cache
function getAccessCache() {
  try {
    const cached = sessionStorage.getItem(ACCESS_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
}

function setAccessCache(routeName, userId, access) {
  try {
    const cache = getAccessCache();
    const key = `${routeName}_${userId}`;
    cache[key] = {
      access,
      timestamp: Date.now()
    };
    sessionStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Failed to cache access check:', error);
  }
}

function getCachedAccess(routeName, userId) {
  try {
    const cache = getAccessCache();
    const key = `${routeName}_${userId}`;
    const entry = cache[key];
    
    if (!entry) return null;
    
    // Check if cache is still valid (within 12 hours)
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      // Remove expired entry
      delete cache[key];
      sessionStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify(cache));
      return null;
    }
    
    return entry.access;
  } catch {
    return null;
  }
}

function clearAccessCache() {
  sessionStorage.removeItem(ACCESS_CACHE_KEY);
}

// === NEW COMPONENT: Handle invite links and redirect to register ===
function InviteRedirect() {
  const { slug } = useParams(); // Get the slug from URL
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) {
      // Store the slug temporarily (or pass via state)
      // Then redirect to registration page with slug in state
      navigate('/register', { 
        state: { inviteSlug: slug },
        replace: true 
      });
    } else {
      navigate('/login', { replace: true });
    }
  }, [slug, navigate]);

  return <div>Redirecting to registration...</div>;
}

// === NEW: Wrapper to pass inviteSlug to AuthForm ===
function RegisterWithInvite({ onLogin }) {
  const location = useLocation();
  const inviteSlug = location.state?.inviteSlug || null;

  return <LazyAuthForm type="register" onLogin={onLogin} initialInviteSlug={inviteSlug} />;
}

function ParentMentorGuard({ children, redirectTo = "/my-student" }) {
  const role = localStorage.getItem("role");
  const location = useLocation();

  if (role === "parent" || role === "mentor") {
    const queryParams = new URLSearchParams(location.search);

    // Resolve viewingChildId: prefer URL state/params, fall back to localStorage
    const viewingChildId =
      location.state?.viewingChildId ||
      queryParams.get("viewingChildId") ||
      localStorage.getItem("viewingChildId"); // ← NEW: localStorage fallback

    if (!viewingChildId || String(viewingChildId).trim() === "") {
      // Clear stale entries if context is truly gone
      localStorage.removeItem("viewingChildId");
      localStorage.removeItem("viewingChildName");
      return <Navigate to={redirectTo} replace />;
    }

    // Persist so refresh/back-navigation doesn't lose context
    localStorage.setItem("viewingChildId", viewingChildId);

    const childName =
      location.state?.childName ||
      decodeURIComponent(queryParams.get("childName") || "") ||
      localStorage.getItem("viewingChildName") ||
      "";
    if (childName) localStorage.setItem("viewingChildName", childName);
  }

  return children;
}

// Lazy load components
const LazyAdminDashboard = React.lazy(() => import('./components/AdminDashboard/AdminDashboard'));
const LazyCareerAssessment = React.lazy(() => import('./components/Assessment/CareerAssessment'));
const LazySubjectAssessment = React.lazy(() => import('./components/Assessment/SubjectAssessment'));
const LazySubjectPart3Assessment = React.lazy(() => import('./components/Assessment/SubjectPart3Assessment'));
const LazyLearningAssessment = React.lazy(() => import('./components/Assessment/LearningAssessment'));
const LazyAIAssessment = React.lazy(() => import('./components/Assessment/AIAssessment'));
const LazyDashboard = React.lazy(() => import('./components/pages/dashboard'));
const LazyDashboardEnrich = React.lazy(() => import('./components/pages/dashboard_enrich'));
const LazyAuthForm = React.lazy(() => import('./components/AuthForm'));
const LazySubjectScoresPage = React.lazy(() => import('./components/scores/SubjectScoresPage'));
const LazyCareerScoresPage = React.lazy(() => import('./components/scores/CareerScoresPage'));
const LazyLearningScoresPage = React.lazy(() => import('./components/scores/LearningScoresPage'));
const LazyFacetManagement = React.lazy(() => import('./components/AdminDashboard/FacetManagement'));
const LazyResetPassword = React.lazy(() => import('./components/ResetPassword'));
const LazyPastChallenges = React.lazy(() => import('./components/Team/PastChallenges'));
const LazySocialFeed = React.lazy(() => import('./components/Social/SocialFeed'));
const LazySubjectConsultantProfile = React.lazy(() => import('./components/Social/SubjectConsultantProfile'));
const LazyJobPostManager = React.lazy(() => import('./components/AdminDashboard/JobPostManager'));
//const LazyFileManagementDashboard = React.lazy(() => import('./components/FileManagementDashboard/FileManagementDashboard'));
//const LazyFileManagementLogin = React.lazy(() => import('./components/FileManagementDashboard/FileManagementLogin'));
//const LazyFileGallery = React.lazy(() => import('./components/Gallery/FileGallery'));
const LazyCareerDashboard = React.lazy(() => import('./components/pages/CareerDashboard'));
const LazySuperCurricularActivityManager = React.lazy(() => import('./components/AdminDashboard/SuperCurricularActivityManager'));
const LazySTARExperienceManager = React.lazy(() => import('./components/AdminDashboard/STARExperienceManager'));
const LazyTopInterviewQuestionsManager = React.lazy(() => import('./components/AdminDashboard/TopInterviewQuestionsManager'));
const LazyRssFeedManager = React.lazy(() => import('./components/AdminDashboard/RssFeedManager'));
const LazyEnrichFeed = React.lazy(() => import('./components/pages/EnrichFeed'));
const LazyPersonalStatementGuidanceManager = React.lazy(() => import('./components/AdminDashboard/PersonalStatementGuidanceManager'));
const LazyMyChildren = React.lazy(() => import('./components/pages/MyChildren')); 
const LazyAcceptInvitePage = React.lazy(() => import('./components/pages/AcceptInvitePage')); 
const LazyLeaderDashboard = React.lazy(() => import('./components/pages/LeaderDashboard'));
const LazyAIScoresPage = React.lazy(() => import('./components/scores/AIScoresPage'));
const LazyAISkillDevelopmentManager = React.lazy(() => import('./components/AdminDashboard/AISkillDevelopmentManager'));
const LazyKnowledgeCMSManager = React.lazy(() => import('./components/AdminDashboard/KnowledgeCMSManager'));
const LazyInvestorDeckManager = React.lazy(() => import('./components/AdminDashboard/InvestorDeckManager'));
const LazyFreeCoursesManager = React.lazy(() => import('./components/AdminDashboard/FreeCoursesManager'));
const LazyStartupXperience = React.lazy(() => import('./components/pages/StartupXperience'));
const LazyStartupTasksCMS = React.lazy(() => import('./components/AdminDashboard/StartupTasksCMS'));
const LazyCoachTheCoachManager = React.lazy(() => import('./components/AdminDashboard/CoachTheCoachManager'));
const LazyCoachTheCoachAssessment = React.lazy(() => import('./components/Assessment/CoachTheCoachAssessment'));
const LazyCoachTheCoachDashboard = React.lazy(() => import('./components/pages/CoachTheCoachDashboard'));
const LazyCoachFacetManagement = React.lazy(() => import('./components/AdminDashboard/CoachFacetManagement'));
const LazyCoachScoresPage = React.lazy(() => import('./components/scores/CoachScoresPage'));
const LazyLearningResourcesManager = React.lazy(() => import('./components/AdminDashboard/LearningResourcesManager'));
const LazyCoachingDailyPractice = React.lazy(() => import('./components/pages/CoachingDailyPractice'));
const LazyCoachDailyPracticeManager = React.lazy(() => import('./components/AdminDashboard/CoachDailyPracticeManager'));
const LazyLeaderQuestionsManager = React.lazy(() => import('./components/AdminDashboard/LeaderQuestionsManager'));
const LazyLeaderFacetManagement = React.lazy(() => import('./components/AdminDashboard/LeaderFacetManagement'));
const LazyLeaderLearningResourcesManager = React.lazy(() => import('./components/AdminDashboard/LeaderLearningResourcesManager'));
const LazyTeamChallengeManager = React.lazy(() => import('./components/AdminDashboard/TeamChallengeManager'));
const LazyLeadershipDailyPracticeManager = React.lazy(() => import('./components/AdminDashboard/LeadershipDailyPracticeManager'));
const LazyVCManager = React.lazy(() => import('./components/AdminDashboard/VCManager'));
const LazyLeadershipAssessment = React.lazy(() => import('./components/Assessment/LeadershipAssessment'));
const LazyLeadershipDashboard = React.lazy(() => import('./components/pages/LeadershipDashboard'));
const LazyLeadershipDailyPractice = React.lazy(() => import('./components/pages/LeadershipDailyPractice'));
const LazyProfileGroupManagement = React.lazy(() => import('./components/AdminDashboard/Profilegroupmanagement'));
const LazySubjectScan = React.lazy(() => import('./components/SubjectScan'));
const LazyLeaderScoresPage = React.lazy(() => import('./components/scores/LeaderScoresPage'));

// Cache for token verification
const tokenCache = new Map();
/*const fileManagementTokenCache = new Map();*/

function ProtectedRoute({ children, isAuthenticated, userId, isAdmin, /*isFileManagementAuthenticated, fileManagementAdminId,*/ requireAuth = true, skipAssessmentCheck = false, }) {
  const [isChecking, setIsChecking] = useState(true);
  const [isValid, setIsValid] = useState(null);
  const [restrictionError, setRestrictionError] = useState('');
  const location = useLocation();

  const checkAccess = useCallback(async () => {
  console.log('Checking access for route:', location.pathname, { 
    isAuthenticated, 
    userId, 
    skipAssessmentCheck
  });

  // Skip assessment check if requested
  if (skipAssessmentCheck) {
    console.log(`Skipping assessment access check for ${location.pathname}`);
    setIsValid(true);
    setIsChecking(false);
    return;
  }

  if (requireAuth && (!isAuthenticated || !userId)) {
    console.log('Main authentication failed:', { isAuthenticated, userId });
    setIsValid(false);
    setIsChecking(false);
    return;
  }

  try {
    const routeToAssessmentType = {
      '/subjectassessment': 'Subject',
      '/subjectpart3assessment': 'SubjectPart3',
      '/careerassessment': 'Career',
      '/learningassessment': 'Learning',
      '/aiassessment': 'AI',
      '/coachthecoachassessment': 'CoachTheCoach',
      '/leadershipassessment': 'Leadership',
      '/subject-scores': null,
      '/career-scores': null,
      '/learning-scores': null,
      '/dashboard': null,
      '/dashboard_enrich': null,
      '/past-challenges': null,
      '/social-feed': null,
      '/consultant-profile': null,
      '/user/:id': null,
      '/CareerDashboard': null,
      '/EnrichFeed': null,
      '/my-student': null,
      '/ai-scores': null,
      '/startup-xperience': null,
      '/CoachTheCoachDashboard': null,
      '/coachthecoach-scores': null,
      '/leader-scores': null,
      '/LeadershipDashboard': null,
    };

    let routeName = location.pathname.split('/')[1];
    let assessmentType = routeToAssessmentType[location.pathname] || null;
    const params = { userId };

    if (location.pathname.startsWith('/user/')) {
      routeName = 'user';
      const targetUserId = location.pathname.split('/')[2];
      params.targetUserId = targetUserId;
    }

    // Check cache first
    const cacheKey = routeName + (params.targetUserId ? `_${params.targetUserId}` : '');
    const cachedAccess = getCachedAccess(cacheKey, userId);
    
    if (cachedAccess !== null) {
      console.log('Using cached access result:', cachedAccess);
      if (cachedAccess) {
        setIsValid(true);
      } else {
        setRestrictionError('Access denied (cached)');
        setIsValid(false);
      }
      setIsChecking(false);
      return;
    }

    if (assessmentType) {
      params.assessmentType = assessmentType;
    }

    console.log('Sending access check request:', { url: `/api/assessment/check-access/${routeName}`, params });
    const response = await axios.get(`/api/assessment/check-access/${routeName}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      params,
    });
    console.log('Access check response:', response.data);

    // Cache the result
    setAccessCache(cacheKey, userId, response.data.access);

    if (response.data.access) {
      setIsValid(true);
    } else {
      setRestrictionError(response.data.error || 'Access denied');
      setIsValid(false);
    }
  } catch (err) {
    console.error('Access check failed:', err.message, err.response?.data);
    setRestrictionError(err.response?.data?.error || 'Failed to verify access');
    setIsValid(false);
  } finally {
    setIsChecking(false);
  }
}, [isAuthenticated, userId, location.pathname, requireAuth, skipAssessmentCheck]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  if (isChecking) {
    return <div>Loading...</div>;
  }

  if (!isValid) {
    console.log('Access denied, redirecting:', { restrictionError, pathname: location.pathname });
    if (restrictionError) {
      alert(restrictionError);
      return <Navigate to="/dashboard" replace />;
    }
      return <Navigate to="/login" replace />;
  }

  return children;
}

function DashboardWrapper(props) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const viewingChildId =
    queryParams.get("viewingChildId") ||
    location.state?.viewingChildId ||
    localStorage.getItem("viewingChildId") || // ← NEW
    null;

  const childName =
    decodeURIComponent(queryParams.get("childName") || "") ||
    location.state?.childName ||
    localStorage.getItem("viewingChildName") || // ← NEW
    null;

  return <LazyDashboard {...props} viewingChildId={viewingChildId} childName={childName} />;
}

function DashboardEnrichWrapper(props) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const viewingChildId =
    queryParams.get("viewingChildId") ||
    location.state?.viewingChildId ||
    localStorage.getItem("viewingChildId") || // ← NEW
    null;

  const childName =
    decodeURIComponent(queryParams.get("childName") || "") ||
    location.state?.childName ||
    localStorage.getItem("viewingChildName") || // ← NEW
    null;

  return <LazyDashboardEnrich {...props} viewingChildId={viewingChildId} childName={childName} />;
}

function AppContent({ isAuthenticated, userId, isAdmin, userRole, /*isFileManagementAuthenticated, fileManagementAdminId,*/  handleUserLogin, handleLogout, /*handleFileManagementLogin*/ }) {
  const navigate = useNavigate();
  const isAdminAuthenticated = useAuth();

  const wrappedHandleUserLogin = useCallback(
    (id, token) => handleUserLogin(id, token, navigate),
    [handleUserLogin, navigate]
  );
  const wrappedHandleLogout = useCallback(() => handleLogout(navigate), [handleLogout, navigate]);
  /*const wrappedHandleFileManagementLogin = useCallback(
    (id, token) => handleFileManagementLogin(id, token, navigate),
    [handleFileManagementLogin, navigate]
  );*/

  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/login" element={<LazyAuthForm type="login" onLogin={wrappedHandleUserLogin} />} />
       <Route
  path="/register"
  element={<RegisterWithInvite onLogin={wrappedHandleUserLogin} />}
/>
        <Route path="/subject-scan" element={<LazySubjectScan />} />
        <Route path="/reset-password" element={<LazyResetPassword />} />
       <Route
  path="/my-student"
  element={
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      userId={userId}
      isAdmin={isAdmin}
      skipAssessmentCheck={true}
    >
      <LazyMyChildren />
    </ProtectedRoute>
  }
/>
<Route
  path="/accept-invite"
  element={
    <ProtectedRoute 
      isAuthenticated={isAuthenticated} 
      userId={userId} 
      isAdmin={isAdmin}
      skipAssessmentCheck={true}
    >
      <LazyAcceptInvitePage />
    </ProtectedRoute>
  }
/>
       <Route
  path="/dashboard"
  element={
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      userId={userId}
      isAdmin={isAdmin}
    >
       <ParentMentorGuard>
        <UIFeedbackProvider>
          <DashboardWrapper
            onLogout={wrappedHandleLogout}
            userId={userId}
            isAdmin={isAdmin}
          />
        </UIFeedbackProvider>
      </ParentMentorGuard>
    </ProtectedRoute>
  }
/>
        <Route
  path="/dashboard_enrich"
  element={
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      userId={userId}
      isAdmin={isAdmin}
      /*isFileManagementAuthenticated={isFileManagementAuthenticated}
      fileManagementAdminId={fileManagementAdminId}*/
    >
       <ParentMentorGuard>
                <UIFeedbackProvider>
      <DashboardEnrichWrapper
        onLogout={wrappedHandleLogout}
        userId={userId}
        isAdmin={isAdmin}
      />
              </UIFeedbackProvider>
      </ParentMentorGuard>
    </ProtectedRoute>
  }
/>
        <Route
  path="/enrichfeed"
  element={
    <UIFeedbackProvider>
    <ProtectedRoute 
    
      isAuthenticated={isAuthenticated} 
      userId={userId} 
      isAdmin={isAdmin} 
      /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
      fileManagementAdminId={fileManagementAdminId}*/
    >
      <LazyEnrichFeed userId={userId} isAdmin={isAdmin} />
    </ProtectedRoute>
    </UIFeedbackProvider>
  }
/>
           <Route
          path="/CareerDashboard"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/>
              <ParentMentorGuard>
                <UIFeedbackProvider>
              <LazyCareerDashboard onLogout={wrappedHandleLogout} userId={userId} isAdmin={isAdmin} role={userRole} />
            </UIFeedbackProvider>
            </ParentMentorGuard>
            </ProtectedRoute>
          }
        />
         <Route
          path="/LeaderDashboard"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin} 
              /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
              fileManagementAdminId={fileManagementAdminId}*/
            >
              
              <ParentMentorGuard>
                        <UIFeedbackProvider>
                <LazyLeaderDashboard 
                  userId={userId} 
                  isAdmin={isAdmin} 
                  role={userRole}
                />
                        </UIFeedbackProvider>
              </ParentMentorGuard>
            </ProtectedRoute>
          }
        />
         <Route
          path="/coaching-daily"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin} 
              /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
              fileManagementAdminId={fileManagementAdminId}*/
            >
              {/* Optional: Block parents/mentors if you want only direct users */}
              {(userRole === 'parent' || userRole === 'mentor') ? (
                <Navigate to="/my-student" replace />
              ) : (
                <UIFeedbackProvider>
                <LazyCoachingDailyPractice 
                  userId={userId} 
                  isAdmin={isAdmin} 
                  role={userRole}
                />
                </UIFeedbackProvider>
              )}
            </ProtectedRoute>
          }
        />
                 <Route
          path="/leadership-daily"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin} 
              /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
              fileManagementAdminId={fileManagementAdminId}*/
            >
              {/* Optional: Block parents/mentors if you want only direct users */}
              {(userRole === 'parent' || userRole === 'mentor') ? (
                <Navigate to="/my-student" replace />
              ) : (
                <UIFeedbackProvider>
                <LazyLeadershipDailyPractice 
                  userId={userId} 
                  isAdmin={isAdmin} 
                  role={userRole}
                />
                </UIFeedbackProvider>
              )}
            </ProtectedRoute>
          }
        />
        <Route
  path="/startup-xperience"
  element={
    <ProtectedRoute 
      isAuthenticated={isAuthenticated} 
      userId={userId} 
      isAdmin={isAdmin} 
      /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
      fileManagementAdminId={fileManagementAdminId}*/
    >
      {/* Block parent/mentor roles entirely — redirect before component mounts */}
      {(userRole === 'parent' || userRole === 'mentor') ? (
        <Navigate to="/login" replace state={{ sidebarItem: 'aiready' }} />
      ) : (
                <UIFeedbackProvider>
        <LazyStartupXperience 
          userId={userId} 
          isAdmin={isAdmin} 
          role={userRole}
        />
                </UIFeedbackProvider>
      )}
    </ProtectedRoute>
  }
/>
  <Route
          path="/CoachTheCoachDashboard"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin} 
              /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
              fileManagementAdminId={fileManagementAdminId}*/
            >
              <ParentMentorGuard>
                <UIFeedbackProvider>
                <LazyCoachTheCoachDashboard 
                  userId={userId} 
                  isAdmin={isAdmin} 
                  role={userRole}
                />
                </UIFeedbackProvider>
              </ParentMentorGuard>
            </ProtectedRoute>
          }
        />
          <Route
          path="/coachthecoach-scores"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin} 
              /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
              fileManagementAdminId={fileManagementAdminId}*/
            >
              <LazyCoachScoresPage userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
  path="/leader-scores"
  element={
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      userId={userId}
      isAdmin={isAdmin}
    >
      <LazyLeaderScoresPage userId={userId} isAdmin={isAdmin} />
    </ProtectedRoute>
  }
/>
         <Route
          path="/LeadershipDashboard"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin} 
              /*isFileManagementAuthenticated={isFileManagementAuthenticated} 
              fileManagementAdminId={fileManagementAdminId}*/
            >
              <ParentMentorGuard>
                   <UIFeedbackProvider>
                <LazyLeadershipDashboard 
                  userId={userId} 
                  isAdmin={isAdmin} 
                  role={userRole}
                />
                </UIFeedbackProvider>
              </ParentMentorGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subjectassessment"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazySubjectAssessment userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subjectpart3assessment"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/>
              <LazySubjectPart3Assessment userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/careerassessment"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazyCareerAssessment userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learningassessment"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazyLearningAssessment userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/aiassessment"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazyAIAssessment userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
  path="/coachthecoachassessment"
  element={
    <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin}>
      <LazyCoachTheCoachAssessment userId={userId} isAdmin={isAdmin} />
    </ProtectedRoute>
  }
/>
        <Route
          path="/leadershipassessment"
          element={
            <ProtectedRoute 
              isAuthenticated={isAuthenticated} 
              userId={userId} 
              isAdmin={isAdmin}
            >
              <LazyLeadershipAssessment userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subject-scores"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazySubjectScoresPage userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/career-scores"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazyCareerScoresPage userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learning-scores"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/ >
              <LazyLearningScoresPage userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
          }
        />
        <Route
  path="/ai-scores"
  element={
    <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/>
      <LazyAIScoresPage userId={userId} isAdmin={isAdmin} />
    </ProtectedRoute>
  }
/>
        <Route
          path="/past-challenges"
          element={
            <UIFeedbackProvider>
            <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} /*isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId}*/>
              <LazyPastChallenges userId={userId} isAdmin={isAdmin} />
            </ProtectedRoute>
            </UIFeedbackProvider>
          }
        />

      
        <Route
          path="/whitelist-manager28976730"
          element={isAdminAuthenticated ? <WhitelistManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/facet-management28976730"
          element={isAdminAuthenticated ? <LazyFacetManagement /> : <Navigate to="/admin-login28976730" replace />}
        />
              <Route
          path="/jobpost-management28976730"
          element={isAdminAuthenticated ? <LazyJobPostManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/supercurricularactivity-management28976730"
          element={isAdminAuthenticated ? <LazySuperCurricularActivityManager /> : <Navigate to="/admin-login28976730" replace />}
        />
         <Route
          path="/star-experience-manager28976730"
          element={isAdminAuthenticated ? <LazySTARExperienceManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/top-interview-questions-manager28976730"
          element={isAdminAuthenticated ? <LazyTopInterviewQuestionsManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/rss-feed-manager28976730"
          element={isAdminAuthenticated ? <LazyRssFeedManager /> : <Navigate to="/admin-login28976730" replace />}
        />
         <Route
          path="/personal-statement-guidance-manager28976730"
          element={isAdminAuthenticated ? <LazyPersonalStatementGuidanceManager /> : <Navigate to="/admin-login28976730" replace />}
        />
           <Route
          path="/ai-skill-development-manager28976730"
          element={isAdminAuthenticated ? <LazyAISkillDevelopmentManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/knowledge-cms-manager28976730"
          element={isAdminAuthenticated ? <LazyKnowledgeCMSManager /> : <Navigate to="/admin-login28976730" replace />}
        />
       <Route
          path="/investor-deck-manager28976730"
          element={isAdminAuthenticated ? <LazyInvestorDeckManager /> : <Navigate to="/admin-login28976730" replace />}
        />
         <Route
          path="/profile-group-management28976730"
          element={isAdminAuthenticated ? <LazyProfileGroupManagement /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/vc-manager28976730"
          element={isAdminAuthenticated ? <LazyVCManager /> : <Navigate to="/admin-login28976730" replace />}
        />
         <Route
          path="/free-courses-manager28976730"
          element={isAdminAuthenticated ? <LazyFreeCoursesManager /> : <Navigate to="/admin-login28976730" replace />}
        />
          <Route
          path="/startup-tasks-cms28976730"
          element={isAdminAuthenticated ? <LazyStartupTasksCMS /> : <Navigate to="/admin-login28976730" replace />}
        />
         <Route
          path="/coach-facet-management28976730"
          element={isAdminAuthenticated ? <LazyCoachFacetManagement /> : <Navigate to="/admin-login28976730" replace />}
        />
          <Route
          path="/coach-the-coach-manager28976730"
          element={isAdminAuthenticated ? <LazyCoachTheCoachManager /> : <Navigate to="/admin-login28976730" replace />}
        />
          <Route
          path="/coach-daily-practice-manager28976730"
          element={isAdminAuthenticated ? <LazyCoachDailyPracticeManager /> : <Navigate to="/admin-login28976730" replace />}
        />
          <Route
          path="/learning-resources-manager28976730"
          element={isAdminAuthenticated ? <LazyLearningResourcesManager /> : <Navigate to="/admin-login28976730" replace />}
        />
          <Route
          path="/leader-questions-manager28976730"
          element={isAdminAuthenticated ? <LazyLeaderQuestionsManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/leader-facet-management28976730"
          element={isAdminAuthenticated ? <LazyLeaderFacetManagement /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/leader-learning-resources-manager28976730"
          element={isAdminAuthenticated ? <LazyLeaderLearningResourcesManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/leadership-daily-practice-manager28976730"
          element={isAdminAuthenticated ? <LazyLeadershipDailyPracticeManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/team-challenge-manager28976730"
          element={isAdminAuthenticated ? <LazyTeamChallengeManager /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
          path="/admin28976730"
          element={isAdminAuthenticated ? <LazyAdminDashboard /> : <Navigate to="/admin-login28976730" replace />}
        />
        <Route
  path="/invite/:slug"
  element={<InviteRedirect />}
/>
         <Route path="/admin-login28976730" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
 /*const [isFileManagementAuthenticated, setIsFileManagementAuthenticated] = useState(false);
  const [fileManagementAdminId, setFileManagementAdminId] = useState(null);*/
  const [isVerifying, setIsVerifying] = useState(true);

  const verifyToken = useCallback(async (token) => {
    console.log('Verifying main token:', token ? 'Token present' : 'No token');
    if (tokenCache.has(token)) {
      const { valid, userId: cachedUserId, isAdmin: cachedIsAdmin, userRole: cachedUserRole } = tokenCache.get(token);
      console.log('Using cached main token:', { valid, cachedUserId, cachedIsAdmin, cachedUserRole });
      setIsAuthenticated(valid);
      setUserId(cachedUserId);
      setIsAdmin(cachedIsAdmin);
      setUserRole(cachedUserRole);
      return valid;
    }

    if (!token) {
      setIsAuthenticated(false);
      setUserId(null);
      setIsAdmin(false);
      setUserRole(null);
      return false;
    }

    try {
      console.log('Sending main verify-token request to: /api/users/verify-token');
      const response = await axios.get(`/api/users/verify-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Main verify token response:', response.data);

      if (response.data.valid) {
        const decoded = jwtDecode(token);
        const storedUserId = response.data.userId || localStorage.getItem('userId');
        setIsAuthenticated(true);
        setUserId(storedUserId);
        setIsAdmin(decoded.role === 'admin');
        setUserRole(decoded.role);
        localStorage.setItem('userId', storedUserId);
        localStorage.setItem('token', token);
        tokenCache.set(token, { valid: true, userId: storedUserId, isAdmin: decoded.role === 'admin', userRole: decoded.role });
        return true;
      } else {
        console.log('Main token invalid:', response.data.error);
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        setIsAuthenticated(false);
        setUserId(null);
        setIsAdmin(false);
        setUserRole(null);
        tokenCache.set(token, { valid: false, userId: null, isAdmin: false, userRole: null });
        return false;
      }
    } catch (err) {
      console.error('Main token verification failed:', err.message, err.response?.data);
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setIsAuthenticated(false);
      setUserId(null);
      setIsAdmin(false);
      setUserRole(null);
      tokenCache.set(token, { valid: false, userId: null, isAdmin: false, userRole: null });
      return false;
    }
  }, []);

  /*const verifyFileManagementToken = useCallback(async (token) => {
    console.log('Verifying file management token:', token ? 'Token present' : 'No token');
    if (fileManagementTokenCache.has(token)) {
      const { valid, fileManagementAdminId } = fileManagementTokenCache.get(token);
      console.log('Using cached file management token:', { valid, fileManagementAdminId });
      setIsFileManagementAuthenticated(valid);
      setFileManagementAdminId(fileManagementAdminId);
      return valid;
    }

    if (!token) {
      setIsFileManagementAuthenticated(false);
      setFileManagementAdminId(null);
      return false;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      console.log('Sending file management verify-token request to:', `${apiUrl}/file-management/verify-token`);
      const response = await axios.get(`${apiUrl}/file-management/verify-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('File management verify token response:', response.data);

      if (response.data.valid) {
        const decoded = jwtDecode(token);
        const storedAdminId = response.data.fileAdminId || localStorage.getItem('fileManagementAdminId');
        setIsFileManagementAuthenticated(true);
        setFileManagementAdminId(storedAdminId);
        localStorage.setItem('fileManagementAdminId', storedAdminId);
        localStorage.setItem('fileManagementToken', token);
        fileManagementTokenCache.set(token, { valid: true, fileManagementAdminId: storedAdminId });
        return true;
      } else {
        console.log('File management token invalid:', response.data.error);
        localStorage.removeItem('fileManagementToken');
        localStorage.removeItem('fileManagementAdminId');
        setIsFileManagementAuthenticated(false);
        setFileManagementAdminId(null);
        fileManagementTokenCache.set(token, { valid: false, fileManagementAdminId: null });
        return false;
      }
    } catch (err) {
      console.error('File management token verification failed:', err.message, err.response?.data);
      localStorage.removeItem('fileManagementToken');
      localStorage.removeItem('fileManagementAdminId');
      setIsFileManagementAuthenticated(false);
      setFileManagementAdminId(null);
      fileManagementTokenCache.set(token, { valid: false, fileManagementAdminId: null });
      return false;
    }
  }, []);*/

    //    existing useEffect that calls verifyToken on mount ⬇️
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const currentToken = localStorage.getItem('token');
          if (currentToken) {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('jwtToken');
            localStorage.removeItem('usertype');
            tokenCache.clear();
            clearAccessCache();
            setIsAuthenticated(false);
            setUserId(null);
            setIsAdmin(false);
            setUserRole(null);
            if (window.location.pathname !== '/login') {
              window.location.replace('/login');
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    /*const fileManagementToken = localStorage.getItem('fileManagementToken');*/
    console.log('Initial verification for tokens:', {
      mainToken: token ? 'Token present' : 'No token',
      /*fileManagementToken: fileManagementToken ? 'Token present' : 'No token',*/
    });
    
    Promise.all([
      verifyToken(token),
      /*verifyFileManagementToken(fileManagementToken),*/
    ]).then(([mainValid/*, fileManagementValid*/]) => {
      console.log('Token verifications complete:', { mainValid/*, fileManagementValid*/ });
      setIsVerifying(false);
    });
  }, [verifyToken/*, verifyFileManagementToken*/]);

 const handleUserLogin = useCallback(async (id, token, navigateFn) => {
  console.log('Handling user login:', { id, token });

  localStorage.setItem('token', token);
  localStorage.setItem('jwtToken', token);
  localStorage.setItem('userId', id);

  const isValid = await verifyToken(token);
  if (!isValid) {
    console.log('Login failed');
    localStorage.clear();
    return;
  }

    // ==================== FETCH & CACHE ROLE + RESTRICTION ====================
   try {
    // First try to get role from JWT (already decoded in verifyToken)
    let decodedToken = null;
    let usertypeFromJWT = null;
    let roleFromJWT = null;
    
    try {
      decodedToken = jwtDecode(token);
      usertypeFromJWT = decodedToken.usertype || null;
      roleFromJWT = decodedToken.role || null;
      
      if (usertypeFromJWT) {
        localStorage.setItem("usertype", usertypeFromJWT);
      }
      console.log("Login usertype from JWT:", usertypeFromJWT);
      console.log("Login role from JWT:", roleFromJWT);
    } catch (err) {
      console.error("Failed to decode token:", err);
    }

    // If we have role from JWT, use it and only fetch app_role if we need last_team_participation
    if (roleFromJWT) {
      // Check if we need team participation data (lazy load later if needed)
      // For now, set the role from JWT
      const appRole = roleFromJWT === 'admin' ? 'admin' : 
                     (roleFromJWT === 'parent' ? 'parent' : 
                     (roleFromJWT === 'mentor' ? 'mentor' : 'user'));
      
      // Cache the role immediately
      localStorage.setItem(`app_role_${id}`, appRole);
      localStorage.setItem(`role_cache_timestamp_${id}`, Date.now().toString());
      
      console.log("✅ Role from JWT cached on login:", appRole);
      setUserRole(appRole);
      
      // Don't fetch app_role endpoint unless we specifically need last_team_participation
      // We'll lazy-load it when needed for team challenge logic
    } else {
      // Fallback: only fetch if role not in JWT
      console.log("Role not found in JWT, fetching from API...");
      const response = await axios.get(`/api/users/${id}/app_role`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const appRole = response.data.app_role || "user";
      // Only cache the role, not last_team_participation (lazy load later)
      localStorage.setItem(`app_role_${id}`, appRole);
      localStorage.setItem(`role_cache_timestamp_${id}`, Date.now().toString());
      
      console.log("✅ Role from API cached on login:", appRole);
      setUserRole(appRole);
    }
    
    // Note: last_team_participation will be lazy-loaded when needed
    // via a helper function that checks for team challenge restrictions
    
  } catch (err) {
    console.error("Failed to fetch role:", err.message);
    // Fallback
    const fallbackRole = "user";
    localStorage.setItem(`app_role_${id}`, fallbackRole);
    localStorage.setItem(`role_cache_timestamp_${id}`, Date.now().toString());
    setUserRole(fallbackRole);
  }
  // =========================================================================

  const userRole = localStorage.getItem(`app_role_${id}`) || "user";
  console.log("Login role detected:", userRole);

  // ==================== COACHPLUS USERTYPE REDIRECT ====================
  const usertype = localStorage.getItem("usertype");
  if (
    usertype === "coachplus" &&
    userRole !== "parent" &&
    userRole !== "mentor"
  ) {
    console.log("CoachPlus user detected — redirecting to /CoachTheCoachDashboard");
    navigateFn("/CoachTheCoachDashboard", {
      replace: true,
      state: { sidebarItem: "coachfeed", directToCoachFeed: true },
    });
    return;
  }
  // ===========================================================================

  
  if (userRole !== "parent" && userRole !== "mentor") {
    try {
      const currentBookmark = localStorage.getItem(`user_bookmark_${id}`);
      console.log("Saved bookmark on login:", currentBookmark);

 if (currentBookmark) {
  let path = "/dashboard";
  let state = {};

  
        // ==================== NEW: COACHING DAILY ====================
        if (currentBookmark.startsWith("coaching-daily_")) {
          path = "/coaching-daily";
          state = { directToCoaching: true };
        }

  // ==================== STARTUP XPERIENCE ====================
  if (currentBookmark.startsWith("startup-xperience_")) {
    const stage = currentBookmark.slice("startup-xperience_".length);
    path = "/startup-xperience";
    state = {
      preselectedStage: stage,
      directToXperience: true,
    };
  }
        else if (currentBookmark.startsWith("profeed_")) {
          const career = currentBookmark.slice("profeed_".length);
          path = "/CareerDashboard";
          state = {
            sidebarItem: "careerfeed",
            preselectedCareer: career,
            directToProfeed: true,
          };
        }

         // ==================== COACH FEED (NEW) ====================
  else if (currentBookmark.startsWith("coachfeed_")) {
    const coachType = currentBookmark.slice("coachfeed_".length); // optional
    path = "/CoachTheCoachDashboard";
    state = {
      sidebarItem: "coachfeed",
      preselectedCoachType: coachType || null,
      directToCoachFeed: true,
    };
  }

        // ==================== DASHBOARD_ENRICH ====================
        else if (currentBookmark.startsWith("knowledge_")) {
          const subject = currentBookmark.slice("knowledge_".length);
          path = "/dashboard_enrich";
          state = {
            sidebarItem: "knowledge",
            preselectedKnowledgeSubject: subject,
            directToKnowledge: true,
          };
        }

else if (currentBookmark.startsWith("acadfeed_")) {
  const subject = currentBookmark.slice("acadfeed_".length);
  path = "/enrichfeed";
  state = { 
    preselectedSubject: subject, 
  };
}

        // ==================== SIMPLE / EXACT BOOKMARKS ====================
        else {
          const routeMap = {
            "aixcel":       { path: "/LeaderDashboard",  state: { sidebarItem: "aixcel" } },
            "launchpad":    { path: "/LeaderDashboard",  state: { sidebarItem: "launchpad" } },
            "profeed":      { path: "/CareerDashboard",  state: { sidebarItem: "careerfeed" } },
            "coachfeed":    { path: "/CoachTheCoachDashboard", state: { sidebarItem: "coachfeed" } },   // ← NEW
            "knowledge":    { path: "/dashboard_enrich", state: { sidebarItem: "knowledge" } },
          };

          const target = routeMap[currentBookmark];
          if (target) {
            path = target.path;
            state = target.state || {};
          }
        }

        console.log(`Redirecting to ${path} with state:`, state);
        navigateFn(path, { replace: true, state });
        return;
      }
    } catch (err) {
      console.error("Bookmark redirect error:", err);
    }
  }

  // Default fallback
  if (userRole === "parent" || userRole === "mentor") {
    navigateFn("/my-student", { replace: true });
  } else {
    navigateFn("/dashboard", { replace: true });
  }

}, [verifyToken]);

  /*const handleFileManagementLogin = useCallback(async (id, token, navigate) => {
    console.log('Handling file management login:', { id, token });
    localStorage.setItem('fileManagementToken', token);
    localStorage.setItem('fileManagementAdminId', id);
    const isValid = await verifyFileManagementToken(token);
    if (isValid) {
      console.log('File management login successful, navigating to /file-management');
      navigate('/file-management', { replace: true });
    } else {
      console.log('File management login failed, clearing storage');
      localStorage.removeItem('fileManagementToken');
      localStorage.removeItem('fileManagementAdminId');
      setIsFileManagementAuthenticated(false);
      setFileManagementAdminId(null);
    }
  }, [verifyFileManagementToken]);*/

  const handleLogout = useCallback((navigate) => {
  const userId = localStorage.getItem('userId');
  
  if (userId) {
    localStorage.removeItem(`app_role_${userId}`);
    localStorage.removeItem(`restriction_until_${userId}`);
    localStorage.removeItem(`role_cache_timestamp_${userId}`);
  }

  console.log('Handling logout');
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('usertype');
  localStorage.removeItem("viewingChildId");
  localStorage.removeItem("viewingChildName");
  
  // Clear all caches
  tokenCache.clear();
  clearAccessCache(); // New: clear access check cache
  
  setIsAuthenticated(false);
  setUserId(null);
  setIsAdmin(false);
  setUserRole(null);
  
  navigate('/login', { replace: true });
}, []);

  const appContentProps = useMemo(
    () => ({
      isAuthenticated,
      userId,
      isAdmin,
      userRole,
      /*isFileManagementAuthenticated,
      fileManagementAdminId,*/
      handleUserLogin,
      handleLogout,
      /*handleFileManagementLogin,*/
    }),
    [isAuthenticated, userId, isAdmin, userRole/*, isFileManagementAuthenticated, fileManagementAdminId*/, handleUserLogin, handleLogout/*, handleFileManagementLogin*/]
  );

  if (isVerifying) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
          <ModalProvider userId={userId}> 
      <AppContent {...appContentProps} />
       </ModalProvider>
    </Router>
  );
}

export default App;


/*<Route
  path="/gallery"
  element={
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      userId={userId}
      isAdmin={isAdmin}
      isFileManagementAuthenticated={isFileManagementAuthenticated}
      fileManagementAdminId={fileManagementAdminId}
      isAdvertisingEngineAuthenticated={isAdvertisingEngineAuthenticated}
      advertisingEngineAdminId={advertisingEngineAdminId}
      requireAuth={false}
    >
      <LazyFileGallery userId={userId} userToken={localStorage.getItem('token')} />
    </ProtectedRoute>
  }
/>

<Route
path="/file-management"
element={
  <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId} isAdvertisingEngineAuthenticated={isAdvertisingEngineAuthenticated} advertisingEngineAdminId={advertisingEngineAdminId}>
    <LazyFileManagementDashboard fileManagementAdminId={fileManagementAdminId} />
  </ProtectedRoute>
}
/>

<Route path="/file-management-login" element={<LazyFileManagementLogin onLogin={wrappedHandleFileManagementLogin} />} />

<Route
path="/social-feed"
element={
  <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId} isAdvertisingEngineAuthenticated={isAdvertisingEngineAuthenticated} advertisingEngineAdminId={advertisingEngineAdminId}>
    <LazySocialFeed userId={userId} userRole={userRole} />
  </ProtectedRoute>
}
/>

<Route
path="/consultant-profile"
element={
  <ProtectedRoute isAuthenticated={isAuthenticated} userId={userId} isAdmin={isAdmin} isFileManagementAuthenticated={isFileManagementAuthenticated} fileManagementAdminId={fileManagementAdminId} isAdvertisingEngineAuthenticated={isAdvertisingEngineAuthenticated} advertisingEngineAdminId={advertisingEngineAdminId}>
    <LazySubjectConsultantProfile userId={userId} userRole={userRole} />
  </ProtectedRoute>
}
/>*/